import type { AssetPickerOption } from "../queries/useAssetPickerQuery";
import type { AssetTreeNode } from "../queries/useAssetTreeQuery";
import type { AssetId } from "./workIds";

/**
 * A picker option with its children attached, keyed by the public `AssetId`
 * (the picker never sees the internal numeric tree ID — see
 * `assetModel.ts`'s `flattenAssetTree`/`orderAssetRows` for the analogous
 * internal-ID-keyed tree used by the Assets module).
 */
export type AssetPickerNode = AssetPickerOption & {
  children: AssetPickerNode[];
};

/**
 * Builds a parent/child tree from the flat picker option list. Options whose
 * `parentId` does not resolve to another option in the list (a root, or an
 * orphaned reference to a record outside this facility/eligibility scope)
 * are treated as root nodes.
 */
export function buildAssetPickerTree(
  options: AssetPickerOption[],
): AssetPickerNode[] {
  const byId = new Map(options.map((option) => [option.id, option]));
  const childrenByParent = new Map<AssetId | null, AssetPickerOption[]>();

  for (const option of options) {
    const parentId =
      option.parentId !== null && byId.has(option.parentId)
        ? option.parentId
        : null;
    const siblings = childrenByParent.get(parentId) ?? [];
    siblings.push(option);
    childrenByParent.set(parentId, siblings);
  }

  function buildChildren(parentId: AssetId | null): AssetPickerNode[] {
    const siblings = childrenByParent.get(parentId) ?? [];
    return siblings.map((option) => ({
      ...option,
      children: buildChildren(option.id),
    }));
  }

  return buildChildren(null);
}

/** Flat index from `AssetId` to its direct parent's `AssetId`, for building
 * ancestor paths without re-walking the tree on every lookup. */
function buildParentIndex(
  options: AssetPickerOption[],
): Map<AssetId, AssetId | null> {
  const byId = new Map(options.map((option) => [option.id, option]));
  const parentIndex = new Map<AssetId, AssetId | null>();
  for (const option of options) {
    const parentId =
      option.parentId !== null && byId.has(option.parentId)
        ? option.parentId
        : null;
    parentIndex.set(option.id, parentId);
  }
  return parentIndex;
}

/**
 * Builds a "Parent > Child" secondary label for a record, by name only —
 * this must never include the record's Number/ID (AssetWatch picker AC: the
 * Number/ID is search-only, never displayed).
 */
export function getAncestorPath(
  options: AssetPickerOption[],
  id: AssetId,
): string {
  const byId = new Map(options.map((option) => [option.id, option]));
  const parentIndex = buildParentIndex(options);
  const names: string[] = [];
  const visited = new Set<AssetId>();
  let currentId: AssetId | null = parentIndex.get(id) ?? null;
  while (currentId !== null && !visited.has(currentId)) {
    visited.add(currentId);
    const current = byId.get(currentId);
    if (!current) break;
    names.unshift(current.name);
    currentId = parentIndex.get(currentId) ?? null;
  }
  return names.join(" > ");
}

/** Match record reads: explicit location first, then the nearest LOCATION,
 * including the selected record itself. */
export function getAssetPickerLocationName(
  options: AssetPickerOption[],
  id: AssetId,
  nodes: AssetTreeNode[] = [],
): string | null {
  const pending = [...nodes];
  while (pending.length > 0) {
    const node = pending.pop();
    if (!node) break;
    if (node.assetId === id && node.locationName) return node.locationName;
    pending.push(...node.children);
  }
  const byId = new Map(options.map((option) => [option.id, option]));
  const visited = new Set<AssetId>();
  let currentId: AssetId | null = id;
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const current = byId.get(currentId);
    if (!current) return null;
    if (current.kind === "LOCATION") return current.name;
    currentId = current.parentId;
  }
  return null;
}
